import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Disclosure } from '@/components/ui/Disclosure'
import { profitSign } from '@/features/cases/lib/profit'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { CaseMoney, Money } from '@/types/domain'

/* The case's one profit line (S-6). It lived in the money section's balance
 * cells until the S-11 revision moved profit to a card of its own on «Обзор»;
 * it sits beside the sections here, in `shared/`, because the overview reads
 * it and `money/` exports its sections and nothing else. */

/** One money figure inside a sentence. Strong is a computed total — emphasis
 *  weight, its currency code muted beside it; a plain term is a figure only.
 *  A loss carries its U+2212 sign from `formatNumber` as well as the negative
 *  tone, so colour is never the only thing saying "loss". */
function InlineFigure({ amount, strong = false }: { amount?: Money; strong?: boolean }) {
  if (!amount) return <span className="asa-num text-text-muted">—</span>

  const loss = profitSign(amount.amount) === 'loss'
  return (
    <span className="whitespace-nowrap">
      <span
        className={cn(
          'asa-num',
          strong && 'font-semibold',
          loss ? 'text-negative' : strong ? 'text-text-heading' : 'text-text-body',
        )}
      >
        {formatNumber(amount.amount, 2)}
      </span>
      {strong ? (
        <>
          {' '}
          <span className="text-text-muted text-[12px] font-normal">{amount.currency}</span>
        </>
      ) : null}
    </span>
  )
}

/** One term of an equation: an operator, a word, and the served figure. */
interface Term {
  op?: '+' | '−'
  label: string
  /** Absent prints a dash — a term the server did not state, or a rate
   *  nobody agreed (which is not a zero). */
  amount?: Money
}

/** «Ставка 34 500.00 − плановые затраты 28 400.00 = 6 100.00 USD».
 *
 *  Every figure is printed as served, the result included: the panel states
 *  the server's arithmetic and never redoes it (AD-9), so terms that do not
 *  balance print as they came rather than as a sum computed here. One currency
 *  code, after the result: the contract serves every term in USD. */
function Equation({ terms, result }: { terms: readonly Term[]; result?: Money }) {
  return (
    <span className="text-text-body text-[13px] leading-relaxed">
      {terms.map((term, index) => (
        <span key={term.label}>
          {index > 0 ? ' ' : null}
          {term.op ? `${term.op} ` : null}
          {term.label} <InlineFigure amount={term.amount} />
        </span>
      ))}{' '}
      = <InlineFigure amount={result} strong />
    </span>
  )
}

/** The case's ONE profit figure (S-6) and the arithmetic behind it.
 *
 *  «Прибыль: план X → прогноз Y» — one word, with the three FR-30 figures told
 *  apart by a qualifier instead of three labels the reader had to rank. The
 *  right side is `profit`, the figure the SERVER chose by the case's status:
 *  the forecast while it runs, the cash (received minus paid) once it is
 *  financially closed or cancelled. `profitBasis` names which, and the
 *  qualifier follows it. Nothing here picks between figures; a block served
 *  without a headline prints a dash rather than borrow the forecast.
 *
 *  `hasRate` still keeps the zeros honest (Story 4.6): with no agreed rate the
 *  plan is «—» and the line says why, while the right side keeps its figure —
 *  a case with costs and no rate is losing money and should say so.
 *
 *  «Как посчитано» is shut by default: the headline is what is read every day,
 *  the terms are what is read when a figure is questioned. It prints only the
 *  served terms (`breakdown`) and results, and is not offered when the block
 *  came without terms. */
export function ProfitLine({
  money,
  headless = false,
}: {
  money: CaseMoney
  /** Under a host that already says «Прибыль» — the overview's card (S-11
   *  revision): the line drops its own lead word and the rule above it. */
  headless?: boolean
}) {
  const { t } = useTranslation()
  const noRate = money.hasRate === false
  const basis = money.profitBasis
  const terms = money.breakdown

  return (
    <div className={cn(!headless && 'border-line-hair border-t')}>
      <div className="flex flex-col gap-1 px-[18px] py-3.5">
        {/* Wraps rather than scrolls: on a phone the right side drops under
            the left, and the arrow travels with it. */}
        <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[13.5px]">
          {headless ? null : (
            <>
              <span className="text-text-heading">{t('cases.money.profit.lead')}</span>{' '}
            </>
          )}
          <span className="flex items-baseline gap-1.5">
            <span className="text-text-muted text-[12.5px]">
              {t('cases.money.profit.planned')}
            </span>{' '}
            {noRate ? (
              <span className="asa-num text-text-muted">—</span>
            ) : (
              <InlineFigure amount={money.expectedProfit} strong />
            )}
          </span>{' '}
          <ArrowRight size={13} className="text-text-faint self-center" aria-hidden />
          <span className="flex items-baseline gap-1.5">
            {basis ? (
              <>
                <span className="text-text-muted text-[12.5px]">
                  {t(`cases.money.profit.${basis}`)}
                </span>{' '}
              </>
            ) : null}
            <InlineFigure amount={money.profit} strong />
          </span>
        </p>
        {noRate ? (
          <span className="text-text-muted text-[12px] leading-snug font-light">
            {t('cases.money.profit.noRate')}
          </span>
        ) : null}
      </div>

      {terms ? (
        /* A footer row of the card rather than a box inside it: no side or
           bottom border, square corners, and a 4px inset so the chevron and
           the body line up with the 18px gutter of the rows above. */
        <Disclosure
          summary={t('cases.money.profit.howCalculated')}
          count={3}
          className="rounded-none border-x-0 border-b-0 px-1"
          bodyClassName="border-t-0 pt-1 pb-4"
        >
          <dl className="flex flex-col gap-3">
            <div className="flex flex-col gap-0.5">
              <dt className="asa-eyebrow">{t('cases.money.profit.rows.planned')}</dt>
              <dd>
                {noRate ? (
                  <span className="text-text-muted text-[13px]">
                    {t('cases.money.profit.noRate')}
                  </span>
                ) : (
                  <Equation
                    terms={[
                      { label: t('cases.money.profit.terms.rate'), amount: terms.rate },
                      {
                        op: '−',
                        label: t('cases.money.profit.terms.plannedCost'),
                        amount: terms.plannedCost,
                      },
                    ]}
                    result={money.expectedProfit}
                  />
                )}
              </dd>
            </div>

            <div className="flex flex-col gap-0.5">
              <dt className="asa-eyebrow">{t('cases.money.profit.rows.forecast')}</dt>
              <dd>
                <Equation
                  terms={[
                    {
                      label: t('cases.money.profit.terms.rate'),
                      amount: noRate ? undefined : terms.rate,
                    },
                    {
                      op: '+',
                      label: t('cases.money.profit.terms.billedExtras'),
                      amount: terms.billedExtras,
                    },
                    {
                      op: '+',
                      label: t('cases.money.profit.terms.debitNoteRecoveries'),
                      amount: terms.debitNoteRecoveries,
                    },
                    { op: '−', label: t('cases.money.profit.terms.cost'), amount: terms.cost },
                  ]}
                  result={money.forecastProfit}
                />
              </dd>
            </div>

            {/* O4: the cash so far is the result only once the case is
                financially closed or cancelled. Before that it is shown under
                a name that does not pretend otherwise, with a note saying when
                it becomes the result. */}
            <div className="flex flex-col gap-0.5">
              <dt className="asa-eyebrow">
                {basis === 'final'
                  ? t('cases.money.profit.rows.final')
                  : t('cases.money.profit.rows.cashToDate')}
              </dt>
              <dd className="flex flex-col gap-0.5">
                <Equation
                  terms={[
                    { label: t('cases.money.profit.terms.received'), amount: terms.received },
                    { op: '−', label: t('cases.money.profit.terms.paid'), amount: terms.paid },
                  ]}
                  result={money.finalProfit}
                />
                {basis === 'forecast' ? (
                  <span className="text-text-muted text-[12px] leading-snug font-light">
                    {t('cases.money.profit.cashToDateNote')}
                  </span>
                ) : null}
              </dd>
            </div>
          </dl>
        </Disclosure>
      ) : null}
    </div>
  )
}
